import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Check, Copy, X } from "lucide-react";
import "./JoinCodeDialog.css";

const focusableElements = (container) =>
  Array.from(
    container?.querySelectorAll('button:not([disabled]), [href], [tabindex]:not([tabindex="-1"])') || []
  );

const CONTENT = {
  teacher: {
    title: "Teacher join code",
    steps: ["Sign in to StudyDen", "Open Join Teacher", "Enter this code and select Join Teacher"],
  },
  class: {
    title: "Class join code",
    steps: [
      "Sign in to StudyDen and join your teacher first",
      "Open My Classes",
      "Enter this code and select Join Class",
    ],
  },
};

// Projector-friendly view of a teacher or class join code. Rendered in a portal
// so the fixed backdrop is never clipped by a transformed sidebar or card.
const JoinCodeDialog = ({ kind, code, className, onClose }) => {
  const dialogRef = useRef(null);
  const closeButtonRef = useRef(null);
  const onCloseRef = useRef(onClose);
  const [copyStatus, setCopyStatus] = useState("");
  const content = CONTENT[kind] || CONTENT.teacher;

  useEffect(() => { onCloseRef.current = onClose; }, [onClose]);

  useEffect(() => {
    const previouslyFocused = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeButtonRef.current?.focus();

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onCloseRef.current();
        return;
      }
      if (event.key !== "Tab") return;
      const elements = focusableElements(dialogRef.current);
      if (elements.length === 0) return;
      const first = elements[0];
      const last = elements[elements.length - 1];
      const active = document.activeElement;
      if (event.shiftKey && (active === first || !dialogRef.current.contains(active))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (active === last || !dialogRef.current.contains(active))) {
        event.preventDefault();
        first.focus();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = previousOverflow;
      if (previouslyFocused instanceof HTMLElement && document.contains(previouslyFocused)) {
        previouslyFocused.focus();
      }
    };
  }, []);

  useEffect(() => {
    if (copyStatus !== "copied") return undefined;
    const timer = window.setTimeout(() => setCopyStatus(""), 1800);
    return () => window.clearTimeout(timer);
  }, [copyStatus]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopyStatus("copied");
    } catch (error) {
      console.error("Could not copy join code", error);
      setCopyStatus("failed");
    }
  };

  return createPortal(
    <div className="join-code-backdrop" role="presentation" onClick={() => onCloseRef.current()}>
      <div
        className="join-code-dialog"
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="join-code-dialog-title"
        aria-describedby="join-code-dialog-instructions"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="join-code-dialog-header">
          <div>
            <h2 id="join-code-dialog-title">{content.title}</h2>
            {kind === "class" && className && <p className="join-code-dialog-class">{className}</p>}
          </div>
          <button
            type="button"
            ref={closeButtonRef}
            className="join-code-dialog-icon-button"
            onClick={() => onCloseRef.current()}
            aria-label="Close"
          >
            <X size={22} />
          </button>
        </div>

        <p className="join-code-dialog-code" style={{ "--join-code-length": Math.max(code.length, 6) }}>
          {code}
        </p>

        <ol className="join-code-dialog-steps" id="join-code-dialog-instructions">
          {content.steps.map((step) => <li key={step}>{step}</li>)}
        </ol>

        <div className="join-code-dialog-actions">
          <span className="join-code-dialog-status" role="status" aria-live="polite">
            {copyStatus === "copied" && "Code copied."}
            {copyStatus === "failed" && "Couldn't copy. Select the code and copy it manually."}
          </span>
          <button type="button" className="join-code-dialog-button join-code-dialog-button-secondary" onClick={() => onCloseRef.current()}>
            Close
          </button>
          <button type="button" className="join-code-dialog-button join-code-dialog-button-primary" onClick={handleCopy}>
            {copyStatus === "copied" ? <Check size={18} /> : <Copy size={18} />}
            {copyStatus === "copied" ? "Copied" : "Copy code"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default JoinCodeDialog;
